// LT-411: unified Agent create / edit form (`/agents/new`, `/agents/:agentId/edit`).
//
// Replaces the legacy `/create-agent` page. L1-only — every field on
// this form lives on `agent_versions` (the reusable persona); L2 brief
// fields (campaign goals, target audience, KPIs, scripts) live on
// Campaign and are edited there.
//
// System presets render the form read-only — backend would 403 on a
// PUT regardless, but the UI surfaces it before the round-trip.

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Bot, Lock, Save, X } from 'lucide-react';

import PageShell from '../../components/PageShell';
import { Button, Card, Input, Pill, Select, Textarea } from '../../ui';
import { useAuth } from '../../context/AuthContext';
import {
  Agent,
  AgentCreate,
  AgentUpdate,
  REQUIRED_CAPABILITIES,
} from '../../types/agent';
import agentsService from '../../services/agentsService';

// LT-411 ticket §form. Snake-cased slugs stored in agents.category;
// labels surface in the dropdown only. Existing seeded categories
// (pre_sales_sdr / collections_specialist / appointment_confirmation
// from LT-338) are not in this list — system presets render their
// existing category as a read-only label.
const CATEGORY_OPTIONS: { value: string; label: string }[] = [
  { value: 'sales_retention', label: 'Sales & Retention' },
  { value: 'collections_payments', label: 'Collections & Payments' },
  { value: 'operations', label: 'Operations' },
  { value: 'insurance_financial', label: 'Insurance & Financial Services' },
  { value: 'education', label: 'Education' },
];

interface FormState {
  name: string;
  description: string;
  category: string;
  voice_id_male: string;
  voice_id_female: string;
  tone: string;
  agent_persona: string;
  objection_handling_style: string;
  prompt_template: string;
}

const EMPTY_FORM: FormState = {
  name: '',
  description: '',
  category: CATEGORY_OPTIONS[0].value,
  voice_id_male: '',
  voice_id_female: '',
  tone: '',
  agent_persona: '',
  objection_handling_style: '',
  prompt_template: '',
};

function formFromAgent(agent: Agent): FormState {
  const v = agent.current_version;
  return {
    name: agent.name,
    description: agent.description ?? '',
    category: agent.category,
    voice_id_male: v?.voice_id_male ?? '',
    voice_id_female: v?.voice_id_female ?? '',
    tone: v?.tone ?? '',
    agent_persona: v?.agent_persona ?? '',
    objection_handling_style: v?.objection_handling_style ?? '',
    prompt_template: v?.prompt_template ?? '',
  };
}

const AgentFormPage: React.FC = () => {
  const { agentId } = useParams<{ agentId?: string }>();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const isEdit = Boolean(agentId);

  const [agent, setAgent] = useState<Agent | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [loading, setLoading] = useState<boolean>(isEdit);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // LT-411: load the agent when in edit mode.
  useEffect(() => {
    if (!isEdit || !agentId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const fetched = await agentsService.get(agentId);
        if (cancelled) return;
        setAgent(fetched);
        setForm(formFromAgent(fetched));
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load agent');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [agentId, isEdit]);

  const isSystemPreset = Boolean(agent?.is_system);
  const readOnly = isSystemPreset; // system presets are immutable
  const canEdit = !isEdit || (!isSystemPreset && (isAdmin || true)); // owner/admin gates live on the backend

  const headerLabel = isEdit ? 'Edit agent' : 'New agent';
  const headerDescription = isEdit
    ? 'Update this agent. Saving creates a new version; sequence steps that pinned the previous version keep firing against it.'
    : 'Build a reusable agent persona. The campaign brief on a Campaign supplies the per-offer detail at call time.';

  const update = <K extends keyof FormState>(key: K) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const trimmedName = form.name.trim();
  const trimmedCategory = form.category.trim();
  const canSubmit = !readOnly && trimmedName.length > 0 && trimmedCategory.length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    setError(null);
    try {
      if (isEdit && agentId && agent) {
        const payload: AgentUpdate = {
          name: trimmedName !== agent.name ? trimmedName : undefined,
          description: form.description !== (agent.description ?? '') ? form.description : undefined,
          prompt_template: form.prompt_template,
          voice_id_male: form.voice_id_male.trim() || null,
          voice_id_female: form.voice_id_female.trim() || null,
          tone: form.tone.trim() || null,
          agent_persona: form.agent_persona.trim() || null,
          objection_handling_style: form.objection_handling_style.trim() || null,
        };
        const updated = await agentsService.update(agentId, payload);
        navigate(`/agents/${updated.id}`);
        return;
      }
      const payload: AgentCreate = {
        name: trimmedName,
        category: trimmedCategory,
        description: form.description.trim() || null,
        prompt_template: form.prompt_template,
        voice_id_male: form.voice_id_male.trim() || null,
        voice_id_female: form.voice_id_female.trim() || null,
        // LT-340 / LT-338 system safety contract — every new agent must
        // carry the required minimum so subsequent edits don't fail
        // capability validation.
        capabilities: [...REQUIRED_CAPABILITIES],
        tone: form.tone.trim() || null,
        agent_persona: form.agent_persona.trim() || null,
        objection_handling_style: form.objection_handling_style.trim() || null,
      };
      const created = await agentsService.create(payload);
      navigate(`/agents/${created.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save agent');
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (isEdit && agentId) {
      navigate(`/agents/${agentId}`);
    } else {
      navigate('/agents');
    }
  };

  // System presets show their existing slug in a disabled select; the new
  // category options aren't mixed in (those are workspace-custom only).
  const categoryOptions = useMemo(() => {
    if (isSystemPreset && agent) {
      return [{ value: agent.category, label: agent.category }];
    }
    if (isEdit && agent && !CATEGORY_OPTIONS.some((c) => c.value === agent.category)) {
      // Workspace-custom agent created with a slug not in the new list
      // (e.g. migrated from UAC with ``pre_sales_sdr``). Surface its
      // existing category alongside the new options.
      return [{ value: agent.category, label: agent.category }, ...CATEGORY_OPTIONS];
    }
    return CATEGORY_OPTIONS;
  }, [agent, isEdit, isSystemPreset]);

  if (isEdit && loading) {
    return (
      <PageShell>
        <div className="text-sm text-slate-400">Loading agent…</div>
      </PageShell>
    );
  }

  if (isEdit && error && !agent) {
    return (
      <PageShell>
        <Card>
          <div className="flex items-start gap-3">
            <X className="text-danger-text" />
            <div>
              <div className="font-medium text-slate-100">Couldn't load this agent</div>
              <div className="text-sm text-slate-400 mt-1">{error}</div>
              <Button intent="secondary" className="mt-4" onClick={() => navigate('/agents')}>
                Back to agents
              </Button>
            </div>
          </div>
        </Card>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <header className="mb-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="rounded-ds-lg bg-surface-2 p-2 text-accent">
              <Bot strokeWidth={2} />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-slate-100">{headerLabel}</h1>
              <p className="mt-1 text-sm text-slate-400 max-w-2xl">{headerDescription}</p>
            </div>
          </div>
          {isSystemPreset && (
            <Pill intent="info" className="flex items-center gap-1">
              <Lock className="h-3 w-3" /> System preset (read-only)
            </Pill>
          )}
        </div>
      </header>

      <form onSubmit={handleSubmit} aria-label="Agent form" data-testid="agent-form">
        <Card>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-200 mb-1">Name *</label>
              <Input
                value={form.name}
                onChange={update('name')}
                disabled={readOnly}
                required
                maxLength={255}
                data-testid="agent-form-name"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-200 mb-1">Description</label>
              <Input
                value={form.description}
                onChange={update('description')}
                disabled={readOnly}
                placeholder="One-line summary — what does this agent do?"
                data-testid="agent-form-description"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-200 mb-1">Category *</label>
              <Select
                value={form.category}
                onChange={update('category')}
                disabled={readOnly || isEdit}
                data-testid="agent-form-category"
              >
                {categoryOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </Select>
              {isEdit && !readOnly && (
                <p className="mt-1 text-xs text-slate-500">
                  Category is set at create time and isn't editable.
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-200 mb-1">Voice (male)</label>
              <Input
                value={form.voice_id_male}
                onChange={update('voice_id_male')}
                disabled={readOnly}
                placeholder="ElevenLabs voice id"
                data-testid="agent-form-voice-male"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-200 mb-1">Voice (female)</label>
              <Input
                value={form.voice_id_female}
                onChange={update('voice_id_female')}
                disabled={readOnly}
                placeholder="ElevenLabs voice id"
                data-testid="agent-form-voice-female"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-200 mb-1">Tone</label>
              <Input
                value={form.tone}
                onChange={update('tone')}
                disabled={readOnly}
                placeholder="e.g. warm and curious; assertive but not pushy"
                data-testid="agent-form-tone"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-200 mb-1">
                Agent persona
              </label>
              <Input
                value={form.agent_persona}
                onChange={update('agent_persona')}
                disabled={readOnly}
                placeholder={`Short self-context the agent uses (e.g. "I'm Alex, an SDR at VoidInc.")`}
                data-testid="agent-form-persona"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-200 mb-1">
                Objection-handling style
              </label>
              <Textarea
                value={form.objection_handling_style}
                onChange={update('objection_handling_style')}
                disabled={readOnly}
                rows={3}
                placeholder="How should the agent respond when pushed back on?"
                data-testid="agent-form-objection-style"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-200 mb-1">Prompt template</label>
              <Textarea
                value={form.prompt_template}
                onChange={update('prompt_template')}
                disabled={readOnly}
                rows={10}
                placeholder="Base instructions for the agent. Variables like {{contact.first_name}} are interpolated at call time."
                data-testid="agent-form-prompt"
              />
            </div>
          </div>

          {error && (
            <div
              className="mt-4 rounded-ds-md border border-danger-border bg-danger-tint p-3 text-sm text-danger-text"
              role="alert"
            >
              {error}
            </div>
          )}
        </Card>

        <div className="mt-4 flex items-center justify-end gap-2">
          <Button intent="ghost" type="button" onClick={handleCancel} disabled={saving}>
            Cancel
          </Button>
          <Button
            intent="primary"
            type="submit"
            disabled={!canSubmit || !canEdit || saving}
            data-testid="agent-form-save"
          >
            <Save className="h-4 w-4" />
            {saving ? 'Saving…' : isEdit ? 'Save version' : 'Create agent'}
          </Button>
        </div>
      </form>
    </PageShell>
  );
};

export default AgentFormPage;
